/**
 * Long product names on phones: two lines and "… more".
 *
 * The cut is CSS (line-clamp on the H1, see pdp-styles), switched on only
 * once this has run -- so with no script the name shows in full. This decides
 * whether the cut is actually hiding anything, by measuring the H1 rather
 * than counting characters: the same name wraps differently on different
 * phones. A name that fits in two lines never gets the button.
 *
 * Open, it closes again when the shopper taps "less", or once the name has
 * scrolled out of view above them -- they have read past it. Never on a
 * timer: the price and buttons below would jump under a finger.
 */
const MOBILE_QUERY = '(max-width: 749px)';

class PdpTitleMore extends HTMLElement {
  #mq = window.matchMedia(MOBILE_QUERY);
  #resizeObserver = new ResizeObserver(() => this.#onResize());
  #intersectionObserver = new IntersectionObserver((entries) => this.#onIntersect(entries));
  #lastWidth = 0;

  connectedCallback() {
    this.heading = this.querySelector('.pdp-title > *');
    this.toggle = this.querySelector('.pdp-title-more__toggle');
    this.label = this.querySelector('.pdp-title-more__label');
    if (!this.heading || !this.toggle) return;

    this.toggleAttribute('data-ready', true);
    this.toggle.addEventListener('click', this.#onClick);
    this.#mq.addEventListener('change', this.#onBreakpoint);
    this.#resizeObserver.observe(this);
    this.#intersectionObserver.observe(this);

    this.#measure();
    // Web fonts can change how many lines the name takes.
    document.fonts?.ready.then(() => this.#measure());
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('click', this.#onClick);
    this.#mq.removeEventListener('change', this.#onBreakpoint);
    this.#resizeObserver.disconnect();
    this.#intersectionObserver.disconnect();
  }

  get #expanded() {
    return this.hasAttribute('data-expanded');
  }

  #setExpanded(expanded) {
    this.toggleAttribute('data-expanded', expanded);
    this.toggle.setAttribute('aria-expanded', String(expanded));
    if (this.label) this.label.textContent = expanded ? 'less' : 'more';
  }

  /* Measured in the collapsed state, then put back as it was -- all in one
     task, so nothing is painted in between. */
  #measure() {
    if (!this.#mq.matches) {
      this.#setExpanded(false);
      this.toggle.hidden = true;
      return;
    }

    const wasExpanded = this.#expanded;
    if (wasExpanded) this.toggleAttribute('data-expanded', false);
    // Half a line of slack: descenders poke a pixel or two past a tight
    // line-height, which is not a hidden line.
    const lineHeight = parseFloat(getComputedStyle(this.heading).lineHeight) || 20;
    const overflowing = this.heading.scrollHeight - this.heading.clientHeight > lineHeight / 2;

    this.toggle.hidden = !overflowing;
    this.#setExpanded(overflowing && wasExpanded);
  }

  #onBreakpoint = () => {
    this.#setExpanded(false);
    this.#measure();
  };

  /* Width only: opening and closing change the height, which needs no
     re-measure. Rotating the phone changes the width, which does. */
  #onResize() {
    const width = this.getBoundingClientRect().width;
    if (width === this.#lastWidth) return;
    this.#lastWidth = width;
    this.#measure();
  }

  #onClick = () => {
    if (!this.#mq.matches) return;
    this.#setExpanded(!this.#expanded);
  };

  /* Close once the whole name has gone off the top of the screen. Closing
     shortens the page above where the shopper is reading, so the scroll is
     corrected to keep what they are looking at still. Browsers with scroll
     anchoring do that themselves; the rest get it done here. */
  #onIntersect(entries) {
    const entry = entries[entries.length - 1];
    if (!entry || entry.isIntersecting || !this.#expanded) return;
    if (entry.boundingClientRect.bottom > 0) return;

    const before = this.offsetHeight;
    this.#setExpanded(false);

    if (!CSS.supports('overflow-anchor', 'auto')) {
      const shift = before - this.offsetHeight;
      if (shift > 0) window.scrollBy(0, -shift);
    }
  }
}

if (!customElements.get('pdp-title-more')) customElements.define('pdp-title-more', PdpTitleMore);
